import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart,
  Wrench,
  Users,
  Package,
  BarChart3,
  Settings,
  Scale,
  Gift,
  UserCog,
  Calendar,
  ShoppingBag,
  Lightbulb,
  ClipboardList,
  Hammer,
  Building2
} from 'lucide-react';
import useEscapeClose from '../hooks/useEscapeClose';
import { API_URL } from '../config/api';
import './Dashboard.css';

const Dashboard = ({ user, onLogout, stores, selectedStore, onStoreChange }) => {
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const profileMenuRef = useRef(null);

  useEscapeClose([
    { state: showHelpModal, setter: setShowHelpModal },
    { state: showProfileMenu, setter: setShowProfileMenu }
  ]);

  // Dışarı tıklanınca menüyü kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Admin kontrolü
  const isAdmin = user?.role === 'admin';
  const isDepo = user?.role === 'depo';

  // Yetki kontrolü - Admin her zaman yetkili, kasiyer/depo ise izne bağlı
  const hasPermission = (permission) => {
    if (isAdmin) return true;
    return user?.permissions?.[permission] === true;
  };

  // Sayfa yolu → permission key eşleştirmesi (depo sorumlusu için)
  const pathToPermission = {
    '/retail-sales': 'page_retail_sales',
    '/repairs': 'page_repairs',
    '/customers': 'page_customers',
    '/products': 'page_products',
    '/reports': 'page_reports',
    '/settings': 'page_settings',
    '/scrap-purchase': 'page_scrap_purchase',
    '/campaigns': 'page_campaigns',
    '/personnel': 'page_personnel',
    '/payment-calendar': 'page_payment_calendar',
    '/wholesaler-payments': 'page_wholesaler_payments',
    '/marketplace': 'page_marketplace'
  };

  const menuCards = [
    {
      id: 1,
      title: 'Perakende Satış',
      description: 'POS sistemi ile hızlı satış işlemleri',
      icon: ShoppingCart,
      path: '/retail-sales',
      color: '#10b981'
    },
    {
      id: 2,
      title: 'Müşteri Yönetimi',
      description: 'Müşteri bilgileri ve CRM',
      icon: Users,
      path: '/customers',
      color: '#8b5cf6'
    },
    {
      id: 3,
      title: 'Ürün Yönetimi',
      description: 'Ürün kataloğu ve fiyatlandırma',
      icon: Package,
      path: '/products',
      color: '#f59e0b'
    },
    {
      id: 4,
      title: 'Hurda Alımı',
      description: 'Altın ve gümüş hurda alım işlemleri',
      icon: Scale,
      path: '/scrap-purchase',
      color: '#eab308'
    },
    {
      id: 5,
      title: 'Tamirat Ürünleri',
      description: 'Müşteriden alınan tamirat kayıtları',
      icon: Wrench,
      path: '/repairs',
      color: '#f97316'
    },
    {
      id: 6,
      title: 'Raporlar',
      description: 'Satış ve stok raporları',
      icon: BarChart3,
      path: '/reports',
      color: '#06b6d4',
      adminOnly: true
    },
    {
      id: 7,
      title: 'Kampanyalar',
      description: 'Aktif kampanyalar ve indirimler',
      icon: Gift,
      path: '/campaigns',
      color: '#ec4899',
      adminOnly: true
    },
    {
      id: 8,
      title: 'Akik Pazar',
      description: 'E-ticaret kontrol paneli (ERP oturumunuzla açılır)',
      icon: ShoppingBag,
      path: '/marketplace',
      color: '#0f766e',
      requiresPermission: 'canAccessShopify'
    },

    {
      id: 9,
      title: 'Ayarlar',
      description: 'Sistem ayarları ve yapılandırma',
      icon: Settings,
      path: '/settings',
      color: '#64748b'
    },
    {
      id: 10,
      title: 'Personel Yönetimi',
      description: 'Personel, yoklama, izin ve maaş takibi',
      icon: UserCog,
      path: '/personnel',
      color: '#6366f1',
      adminOnly: true
    },
    {
      id: 11,
      title: 'Ödemeler & Takvim',
      description: 'Ödeme planları, hatırlatıcılar ve takvim',
      icon: Calendar,
      path: '/payment-calendar',
      color: '#14b8a6',
      adminOnly: true
    },
    {
      id: 12,
      title: 'Toptancı Ödemeleri',
      description: 'Toptancı ve tedarikçi ödeme takibi',
      icon: Building2,
      path: '/wholesaler-payments',
      color: '#0f766e',
      adminOnly: true
    }
  ];

  // Depo mağazası kontrolü (store_id: 4 = Akik Gümüş Depo)
  const currentStoreId = isAdmin ? selectedStore?.id : user?.store_id;
  const isDepoStore = currentStoreId === 4;

  // En geç teslim süresi gelmiş/geçmiş tamirat sayısı (Tamirat Ürünleri kartında rozet için)
  const [repairOverdueCount, setRepairOverdueCount] = useState(0);
  useEffect(() => {
    const fetchOverdueRepairCount = async () => {
      try {
        const params = currentStoreId ? `?storeId=${currentStoreId}` : '';
        const response = await fetch(`${API_URL}/repairs/overdue-count${params}`);
        const data = await response.json();
        setRepairOverdueCount(data.count || 0);
      } catch (error) {
        console.error('Süresi geçen tamirat sayısı alınamadı:', error);
      }
    };
    fetchOverdueRepairCount();
    const interval = setInterval(fetchOverdueRepairCount, 30000);
    return () => clearInterval(interval);
  }, [currentStoreId]);

  // Kart görünürlük kontrolü
  const visibleCards = menuCards.filter(card => {
    // Depo mağazasında admin değilse ve depo rolü değilse sadece Ürün Yönetimi ve Ayarlar görünsün
    if (isDepoStore && !isAdmin && !isDepo && card.id !== 4 && card.id !== 6) return false;
    // Depo sorumlusu - sadece izin verilen sayfalar
    if (isDepo) {
      const permKey = pathToPermission[card.path];
      return permKey ? user?.permissions?.[permKey] === true : false;
    }
    // Admin only kartlar sadece admin görebilir
    if (card.adminOnly) return isAdmin;
    // Yetki gerektiren kartlar için yetki kontrolü
    if (card.requiresPermission) return hasPermission(card.requiresPermission);
    // Diğer kartlar herkese açık
    return true;
  });

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Akik"
            className="header-logo"
          />
        </div>
        <div className="header-right">
          {/* Mağaza Seçici - Sadece admin için */}
          {isAdmin && stores?.length > 0 && (
            <select
              className="store-selector"
              value={selectedStore?.id || ''}
              onChange={(e) => onStoreChange(e.target.value)}
            >
              {stores.map(store => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          )}

          <div className="header-user" ref={profileMenuRef}>
            <div
              className="user-avatar"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
            >
              {user?.name?.charAt(0) || 'U'}
            </div>

          {showProfileMenu && (
            <div className="profile-dropdown">
              <div className="profile-info">
                <span className="profile-name">{user?.name || 'Kullanıcı'}</span>
                <span className="profile-email">{user?.email || ''}</span>
                <span className="profile-role">{user?.role === 'admin' ? 'Yönetici' : user?.role === 'depo' ? 'Depo Sorumlusu' : 'Kullanıcı'}</span>
              </div>
              <div className="profile-divider"></div>
              <button className="profile-menu-item" onClick={() => { navigate('/settings'); setShowProfileMenu(false); }}>
                Hesap Ayarları
              </button>
              <button className="profile-menu-item logout" onClick={onLogout}>
                Çıkış Yap
              </button>
            </div>
          )}
          </div>
        </div>
      </header>

      <main className="dashboard-content">
        <button className="help-btn-corner" title="Yardım" onClick={() => setShowHelpModal(true)}>?</button>
        <div className="cards-container">
          {visibleCards.map((card) => (
            <div
              key={card.id}
              className={`menu-card ${card.disabled ? 'disabled' : ''}`}
              onClick={() => !card.disabled && card.path && navigate(card.path)}
              style={{ '--card-color': card.color }}
            >
              <div className="card-icon" style={{ backgroundColor: `${card.color}15`, color: card.color }}>
                <card.icon size={28} strokeWidth={1.5} />
              </div>
              {card.id === 5 && repairOverdueCount > 0 && (
                <span className="card-count-badge">{repairOverdueCount}</span>
              )}
              <h2 className="card-title">{card.title}</h2>
              <p className="card-description">{card.description}</p>
              <div className="card-arrow">→</div>
            </div>
          ))}
        </div>
      </main>

      <footer className="dashboard-footer">
        <p>© 2025 <a href="https://www.qubite.net/" target="_blank" rel="noopener noreferrer">Qubite ERP ve Perakende Çözümleri</a> ile geliştirilmiştir</p>
      </footer>

      {/* Yardım Modal */}
      {showHelpModal && (
        <div className="modal-overlay">
          <div className="modal help-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header help-header">
              <h2>Akik ERP Yardım Rehberi</h2>
              <button className="close-btn" onClick={() => setShowHelpModal(false)}>✕</button>
            </div>
            <div className="help-modal-content">
              {/* Hoş Geldiniz */}
              <div className="help-section welcome-section">
                <h3>Hoş Geldiniz!</h3>
                <p>Akik Gümüş & Jewellery ERP sistemi, mağazanızın tüm operasyonlarını tek bir yerden yönetmenizi sağlar. Aşağıda her modülün ne işe yaradığını bulabilirsiniz.</p>
              </div>

              {/* Perakende Satış */}
              <div className="help-section">
                <h3><ShoppingCart size={20} className="help-section-icon" /> Perakende Satış</h3>
                <p>POS sistemi ile hızlı satış yapabilirsiniz.</p>
                <ul className="help-feature-list">
                  <li>Barkod veya ürün kodu ile ürün ekleme</li>
                  <li>Nakit, kredi kartı, havale ile ödeme</li>
                  <li>Kampanya ve indirim uygulama</li>
                  <li>Fiş ve fatura yazdırma</li>
                </ul>
              </div>

              {/* Tamirat Ürünleri */}
              <div className="help-section">
                <h3><Wrench size={20} className="help-section-icon" /> Tamirat Ürünleri</h3>
                <p>Müşteriden alınan tamir/tamirat işlerini takip edin.</p>
                <ul className="help-feature-list">
                  <li>Tamirat kaydı oluşturma</li>
                  <li>Durum takibi (teslim alındı, işlemde, tamamlandı)</li>
                  <li>Fotoğraf ekleme</li>
                  <li>Müşteriye fiş verme</li>
                </ul>
              </div>

              {/* Müşteri Yönetimi */}
              <div className="help-section">
                <h3><Users size={20} className="help-section-icon" /> Müşteri Yönetimi</h3>
                <p>Müşteri bilgilerini ve satın alma geçmişini yönetin.</p>
                <ul className="help-feature-list">
                  <li>Müşteri kayıt ve düzenleme</li>
                  <li>Satın alma geçmişi görüntüleme</li>
                  <li>Bakiye ve puan takibi</li>
                  <li>Doğum günü hatırlatmaları</li>
                </ul>
              </div>

              {/* Ürün Yönetimi */}
              <div className="help-section">
                <h3><Package size={20} className="help-section-icon" /> Ürün Yönetimi</h3>
                <p>Ürün kataloğu ve stok yönetimi.</p>
                <ul className="help-feature-list">
                  <li>Ürün ekleme ve düzenleme</li>
                  <li>Kategori yönetimi</li>
                  <li>Fiyat güncelleme</li>
                  <li>Stok takibi</li>
                </ul>
              </div>

              {/* Hurda Alımı */}
              <div className="help-section">
                <h3><Scale size={20} className="help-section-icon" /> Hurda Alımı</h3>
                <p>Altın ve gümüş hurda alım işlemleri.</p>
                <ul className="help-feature-list">
                  <li>Hurda kaydı oluşturma</li>
                  <li>Gram ve ayar hesaplama</li>
                  <li>Güncel kur ile fiyatlandırma</li>
                  <li>Ödeme kaydı tutma</li>
                </ul>
              </div>

              {/* Kampanyalar */}
              <div className="help-section">
                <h3><Gift size={20} className="help-section-icon" /> Kampanyalar</h3>
                <p>İndirim ve kampanya yönetimi.</p>
                <ul className="help-feature-list">
                  <li>Yüzde veya tutar indirimi</li>
                  <li>X al Y öde kampanyaları</li>
                  <li>Hediye kartı oluşturma</li>
                  <li>Özel gün kampanyaları</li>
                </ul>
              </div>

              {/* Raporlar */}
              <div className="help-section">
                <h3><BarChart3 size={20} className="help-section-icon" /> Raporlar</h3>
                <p>Satış ve stok raporları.</p>
                <ul className="help-feature-list">
                  <li>Günlük/haftalık/aylık satış raporu</li>
                  <li>Ürün bazlı satış analizi</li>
                  <li>Kasa raporu</li>
                  <li>Excel'e aktarma</li>
                </ul>
              </div>

              {/* Diğer Modüller */}
              <div className="help-section">
                <h3>Diğer Modüller</h3>
                <div className="help-other-modules">
                  <div className="help-module-item">
                    <span className="module-icon"><UserCog size={24} /></span>
                    <div>
                      <strong>Personel Yönetimi</strong>
                      <p>Personel, yoklama, izin ve maaş takibi (Sadece yönetici)</p>
                    </div>
                  </div>
                  <div className="help-module-item">
                    <span className="module-icon"><Calendar size={24} /></span>
                    <div>
                      <strong>Ödemeler & Takvim</strong>
                      <p>Ödeme planları ve hatırlatıcılar</p>
                    </div>
                  </div>
                  <div className="help-module-item">
                    <span className="module-icon"><Settings size={24} /></span>
                    <div>
                      <strong>Ayarlar</strong>
                      <p>Sistem ayarları, kullanıcı yönetimi, 2FA ve yedekleme</p>
                    </div>
                  </div>
                  <div className="help-module-item">
                    <span className="module-icon"><ClipboardList size={24} /></span>
                    <div>
                      <strong>Stok Sayım</strong>
                      <p>Mağaza bazlı stok sayımı ve fark raporları</p>
                    </div>
                  </div>
                  <div className="help-module-item">
                    <span className="module-icon"><Hammer size={24} /></span>
                    <div>
                      <strong>Atölye</strong>
                      <p>Tamirat işlerinin atölye tarafından takibi</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* İpuçları */}
              <div className="help-section tips-section">
                <h3><Lightbulb size={20} className="help-section-icon" /> Genel İpuçları</h3>
                <ul className="help-tips-list">
                  <li>Her sayfada sağ üstte (?) butonuna tıklayarak o sayfaya özel yardım alabilirsiniz</li>
                  <li>İşlem geçmişi butonları ile yapılan değişiklikleri takip edebilirsiniz</li>
                  <li>Verileri Excel'e aktararak yedek alabilirsiniz</li>
                  <li>Sorun yaşarsanız sistem yöneticinize başvurun</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
